import { useEffect, useMemo } from 'react'
import moment from 'moment'
import {
  useDashboardFetchWaves,
  useReportDashboardFetchWave,
  waveFetchState,
} from '@context/DashboardFetchWaveContext'
import { earliestWave } from '@context/dashboardFetchWave'
import type { RangeKey } from '@dataTypes/dashboard.types'
import {
  buildDashboardDetailHref,
  DASHBOARD_DETAIL_CONTEXT,
} from '@constants/Dashboard/dashboardDetailContext.util'
import { DASHBOARD_DETAIL_HREFS } from '@constants/Dashboard/dashboardDetailRoutes.constants'
import { useNotificationToast } from '@context/NotificationToastContext'
import { useARAgingOverview } from '@hooks/dashboard/useARAging'
import { useAROver90Overview } from '@hooks/dashboard/useAROver90'
import { useCollectionOverview } from '@hooks/dashboard/useCollection'
import {
  FINANCE_PRODUCTION_BY_PROCEDURE_CARD_CATEGORY_LIMIT,
  useFinanceProductionByProcedureOverview,
} from '@hooks/dashboard/useFinanceProductionByProcedure'
import { useFinanceProductionPerProviderOverview } from '@hooks/dashboard/useFinanceProductionPerProvider'
import { useProductionByDepartmentOverview } from '@hooks/dashboard/useProductionByDepartment'
import { useDashboardUtilizationData } from '@hooks/dashboard/useDashboardUtilizationData'
import { useSchedulingPerformanceOverview } from '@hooks/dashboard/useSchedulingPerformanceOverview'
import { useSeatUtilizationOverview } from '@hooks/dashboard/useSeatUtilization'
import {
  useDashboardPeriodFetch,
  useDashboardPeriodParams,
} from '@hooks/dashboard/useDashboardPeriod'
import { useClinicGoal } from '@hooks/dashboard/useClinicGoal'
import {
  getProductionOverview,
  type ProductionDashboardResult,
} from '@services/api/dashboard/finance.api'
import {
  CALENDAR_DATE_FORMAT,
  formatChartCurrencyKUnits,
  formatGoalCurrencyLabel,
  formatPercent,
  toGoalNumber,
} from '@utils/dashboardFinance.util'
import { createFailedToast } from '@utils/notificationToast.util'
import { isDashboardDevMocksEnabled } from '@utils/dashboardDevMocks.util'
import { getFinanceProductionByProcedurePanel } from '@components/pages/Dashboard/Finance/financeMockData'
import {
  buildPerformanceSectionWaves,
  type PerformanceSectionWaves,
} from './performanceSectionWaves'

export type { PerformanceSectionWaves } from './performanceSectionWaves'

export type DsoGroupedBarChartPayload = {
  data: { label: string; current: number; target: number }[]
  yDomain: [number, number]
  yTicks: number[]
}

type UsePerformanceDashboardOptions = {
  range: RangeKey
  date: string
  hiddenSections: Set<string>
  providerSearch: string
}

function buildDsoGroupedBarChart(
  summary:
    | {
        patientDaysToCollect: { current: number }
        insuranceDaysToCollect: { current: number }
      }
    | undefined,
  patientTargetDays: number,
  insuranceTargetDays: number,
): DsoGroupedBarChartPayload {
  const patientCurrent = Math.round(summary?.patientDaysToCollect.current ?? 0)
  const insuranceCurrent = Math.round(
    summary?.insuranceDaysToCollect.current ?? 0,
  )
  const data = [
    { label: 'Patient', current: patientCurrent, target: patientTargetDays },
    {
      label: 'Insurance',
      current: insuranceCurrent,
      target: insuranceTargetDays,
    },
  ]
  const maxVal = Math.max(
    patientCurrent,
    insuranceCurrent,
    patientTargetDays,
    insuranceTargetDays,
    1,
  )
  const top = Math.max(20, Math.ceil((maxVal * 1.2) / 5) * 5)
  const yDomain: [number, number] = [0, top]
  const step = top / 4
  const yTicks = [0, step, step * 2, step * 3, top].map((v) => Math.round(v))

  return { data, yDomain, yTicks }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type -- inferred; exported hook is typed below
function usePerformanceDashboardImpl({
  range,
  date,
  hiddenSections,
  providerSearch,
}: UsePerformanceDashboardOptions) {
  const waves: PerformanceSectionWaves =
    buildPerformanceSectionWaves(hiddenSections)
  const openWaves = useDashboardFetchWaves()
  const productionGate = waveFetchState(openWaves, waves.production)
  const productionByProcedureGate = waveFetchState(
    openWaves,
    waves.productionByProcedure,
  )
  const collectionGate = waveFetchState(openWaves, waves.collection)
  const arAgingGate = waveFetchState(openWaves, waves.arAging)
  const arOver90Gate = waveFetchState(openWaves, waves.arOver90)
  const productionByDepartmentGate = waveFetchState(
    openWaves,
    waves.productionByDepartment,
  )
  const clinicGoalGate = waveFetchState(
    openWaves,
    earliestWave([waves.production, waves.collection]),
  )
  const clinicalEfficiencyGate = waveFetchState(
    openWaves,
    waves.clinicalEfficiency,
  )
  const schedulingGate = waveFetchState(openWaves, waves.scheduling)
  const utilizationMetricsGate = waveFetchState(
    openWaves,
    waves.utilizationMetrics,
  )
  const seatUtilizationGate = waveFetchState(openWaves, waves.seatUtilization)
  const providerUtilizationGate = waveFetchState(
    openWaves,
    waves.providerUtilization,
  )
  const productionPerProviderGate = waveFetchState(
    openWaves,
    waves.productionPerProvider,
  )
  const utilizationApi = useDashboardUtilizationData({
    range,
    date,
    fetchFlags: {
      scheduling: utilizationMetricsGate.enabled,
      providerUtilization: providerUtilizationGate.enabled,
      clinicalEfficiency:
        clinicalEfficiencyGate.enabled || utilizationMetricsGate.enabled,
      diagnosedTreatment: utilizationMetricsGate.enabled,
      caseAcceptance: utilizationMetricsGate.enabled,
    },
    queuedFlags: {
      scheduling: utilizationMetricsGate.queued,
      providerUtilization: providerUtilizationGate.queued,
      clinicalEfficiency:
        clinicalEfficiencyGate.queued || utilizationMetricsGate.queued,
      diagnosedTreatment: utilizationMetricsGate.queued,
      caseAcceptance: utilizationMetricsGate.queued,
    },
  })
  const schedulingPerformanceApi = useSchedulingPerformanceOverview(
    range,
    date,
    schedulingGate.enabled,
    schedulingGate.queued,
  )
  const seatUtilizationApi = useSeatUtilizationOverview({
    range,
    date,
    enabled: seatUtilizationGate.enabled,
    queued: seatUtilizationGate.queued,
  })
  const prodPerProviderApi = useFinanceProductionPerProviderOverview({
    range,
    date,
    providerSearch,
    enabled: productionPerProviderGate.enabled,
    queued: productionPerProviderGate.queued,
  })
  const prodByProcedureApi = useFinanceProductionByProcedureOverview({
    range,
    date,
    maxChartCategories: FINANCE_PRODUCTION_BY_PROCEDURE_CARD_CATEGORY_LIMIT,
    enabled: productionByProcedureGate.enabled,
    queued: productionByProcedureGate.queued,
  })
  const prodByDeptApi = useProductionByDepartmentOverview({
    range,
    date,
    enabled: productionByDepartmentGate.enabled,
    queued: productionByDepartmentGate.queued,
  })
  const collectionApi = useCollectionOverview({
    range,
    date,
    enabled: collectionGate.enabled,
    queued: collectionGate.queued,
  })
  const arAgingApi = useARAgingOverview({
    range,
    date,
    enabled: arAgingGate.enabled,
    queued: arAgingGate.queued,
  })
  const arOver90Api = useAROver90Overview({
    range,
    date,
    enabled: arOver90Gate.enabled,
    queued: arOver90Gate.queued,
  })
  const productionQueryParams = useDashboardPeriodParams(range, date)
  const {
    data: productionData,
    loading: productionLoading,
    error: productionError,
  } = useDashboardPeriodFetch<ProductionDashboardResult>(
    productionQueryParams,
    getProductionOverview,
    { enabled: productionGate.enabled, queued: productionGate.queued },
  )
  const { goal: clinicGoal, loading: clinicGoalLoading } = useClinicGoal({
    range,
    date,
    enabled: clinicGoalGate.enabled,
    queued: clinicGoalGate.queued,
  })

  const productionPanel = useMemo(() => {
    const gross = productionData?.summary.grossProduction.current ?? 0
    const net = productionData?.summary.netProduction.current ?? 0
    const adjustmentPct =
      productionData?.summary.adjustmentPercentage.current ?? 0
    const grossDelta =
      productionData?.summary.grossProduction.percentChange ?? 0
    const prevGross = productionData?.summary.grossProduction.previous ?? 0
    const deltaAmt = gross - prevGross
    const changeSignedPct = `${grossDelta >= 0 ? '+' : ''}${Math.abs(grossDelta).toFixed(0)}%`
    const changeAmt = `$${Math.round(deltaAmt).toLocaleString('en-US')}`
    return {
      prodGoalLabel: formatGoalCurrencyLabel(clinicGoal?.gross_production_goal),
      changeSignedPct,
      changeAmt,
      gross: formatChartCurrencyKUnits(gross / 1000),
      net: formatChartCurrencyKUnits(net / 1000),
      adjustment: formatPercent(adjustmentPct, 1),
      // Raw values for empty-state checks (display strings above are formatted).
      grossValue: gross,
      netValue: net,
      adjustmentValue: adjustmentPct,
      hasPayload: productionData != null,
    }
  }, [productionData, clinicGoal])

  const dsoBarChart = useMemo((): DsoGroupedBarChartPayload => {
    const patientTarget = toGoalNumber(
      clinicGoal?.patient_days_to_collect_target,
    )
    const insuranceTarget = toGoalNumber(
      clinicGoal?.insurance_days_to_collect_target,
    )
    return buildDsoGroupedBarChart(
      collectionApi.summary,
      patientTarget,
      insuranceTarget,
    )
  }, [collectionApi.summary, clinicGoal])

  const hrefs = useMemo(() => {
    const detailParams = {
      context: DASHBOARD_DETAIL_CONTEXT.performance,
      range,
      date: moment(date, CALENDAR_DATE_FORMAT).format('YYYY-MM-DD'),
    }
    return {
      productionByProcedure: buildDashboardDetailHref(
        DASHBOARD_DETAIL_HREFS.financeProductionByProcedure,
        detailParams,
      ),
      productionByDepartment: buildDashboardDetailHref(
        DASHBOARD_DETAIL_HREFS.financeProductionByDepartment,
        detailParams,
      ),
      productionPerProvider: buildDashboardDetailHref(
        DASHBOARD_DETAIL_HREFS.financeProductionPerProvider,
        detailParams,
      ),
      collectionRatio: buildDashboardDetailHref(
        DASHBOARD_DETAIL_HREFS.financeCollectionRatioPerformance,
        detailParams,
      ),
      collection: buildDashboardDetailHref(
        DASHBOARD_DETAIL_HREFS.financeCollectionPerformance,
        detailParams,
      ),
      arAging: buildDashboardDetailHref(
        DASHBOARD_DETAIL_HREFS.arAging,
        detailParams,
      ),
      arOver90Day: buildDashboardDetailHref(
        DASHBOARD_DETAIL_HREFS.arOver90Day,
        detailParams,
      ),
      seatUtilization: buildDashboardDetailHref(
        DASHBOARD_DETAIL_HREFS.seatUtilization,
        detailParams,
      ),
      schedulingPerformance: buildDashboardDetailHref(
        DASHBOARD_DETAIL_HREFS.schedulingPerformance,
        detailParams,
      ),
      clinicalEfficiency: buildDashboardDetailHref(
        DASHBOARD_DETAIL_HREFS.clinicalEfficiency,
        detailParams,
      ),
    }
  }, [range, date])

  const useDashboardMocks = isDashboardDevMocksEnabled()

  const prodByProcedureUsesMocks =
    useDashboardMocks &&
    !prodByProcedureApi.loading &&
    (!!prodByProcedureApi.error || prodByProcedureApi.rows.length === 0)

  const productionPanelForProcedure = useMemo(() => {
    if (!useDashboardMocks) {
      return productionPanel
    }
    const shouldUseMockPanel =
      prodByProcedureUsesMocks ||
      (!productionLoading &&
        (productionError || productionPanel.gross === '$0'))
    if (!shouldUseMockPanel) {
      return productionPanel
    }
    const mock = getFinanceProductionByProcedurePanel(range)
    return {
      ...productionPanel,
      changeSignedPct: mock.changeSignedPct,
      changeAmt: mock.changeAmt,
      prodGoalLabel: mock.goalLabel,
      gross: mock.gross,
      net: mock.net,
      adjustment: mock.adjProductionRatio,
    }
  }, [
    useDashboardMocks,
    prodByProcedureUsesMocks,
    productionLoading,
    productionError,
    productionPanel,
    range,
  ])

  const loading =
    prodByProcedureApi.loading ||
    prodByDeptApi.loading ||
    collectionApi.loading ||
    arAgingApi.loading ||
    arOver90Api.loading ||
    productionLoading ||
    utilizationApi.clinicalEfficiencyLoading ||
    utilizationApi.schedulingLoading ||
    utilizationApi.diagnosedTreatmentLoading ||
    utilizationApi.caseAcceptanceLoading ||
    utilizationApi.providerUtilizationLoading ||
    schedulingPerformanceApi.loading ||
    seatUtilizationApi.loading ||
    prodPerProviderApi.loading

  const hasError =
    (!prodByProcedureUsesMocks &&
      !prodByProcedureApi.loading &&
      !!prodByProcedureApi.error) ||
    (!prodByDeptApi.loading && !!prodByDeptApi.error) ||
    (!collectionApi.loading && !!collectionApi.error) ||
    (!arAgingApi.loading && !!arAgingApi.error) ||
    (!arOver90Api.loading && !!arOver90Api.error) ||
    (!productionLoading && !!productionError) ||
    (!schedulingPerformanceApi.loading && !!schedulingPerformanceApi.error) ||
    (!seatUtilizationApi.loading && !!seatUtilizationApi.error) ||
    (!prodPerProviderApi.loading && !!prodPerProviderApi.error)

  const goalWave = earliestWave([waves.production, waves.collection])
  useReportDashboardFetchWave(
    0,
    (waves.productionByProcedure === 0 && prodByProcedureApi.loading) ||
      (waves.production === 0 && productionLoading) ||
      (waves.clinicalEfficiency === 0 &&
        utilizationApi.clinicalEfficiencyLoading) ||
      (waves.scheduling === 0 && schedulingPerformanceApi.loading) ||
      (goalWave === 0 && clinicGoalLoading),
  )
  useReportDashboardFetchWave(
    1,
    (waves.collection === 1 && collectionApi.loading) ||
      (waves.arAging === 1 && arAgingApi.loading) ||
      (waves.arOver90 === 1 && arOver90Api.loading) ||
      (waves.utilizationMetrics === 1 &&
        (utilizationApi.schedulingLoading ||
          utilizationApi.clinicalEfficiencyLoading ||
          utilizationApi.diagnosedTreatmentLoading ||
          utilizationApi.caseAcceptanceLoading)) ||
      (goalWave === 1 && clinicGoalLoading),
  )
  useReportDashboardFetchWave(
    2,
    (waves.productionByDepartment === 2 && prodByDeptApi.loading) ||
      (waves.collection === 2 && collectionApi.loading) ||
      (waves.seatUtilization === 2 && seatUtilizationApi.loading) ||
      (waves.providerUtilization === 2 &&
        utilizationApi.providerUtilizationLoading) ||
      (waves.productionPerProvider === 2 && prodPerProviderApi.loading) ||
      (goalWave === 2 && clinicGoalLoading),
  )

  const { showToast } = useNotificationToast()

  useEffect(() => {
    if (!loading && hasError) {
      showToast(
        createFailedToast(
          'Failed to load metric',
          'Failed to load performance dashboard data. Please try again.',
        ),
      )
    }
  }, [loading, hasError, showToast])

  return {
    range,
    hrefs,
    loading,
    hasError,
    prodByProcedureApi,
    prodByDeptApi,
    collectionApi,
    arAgingApi,
    arOver90Api,
    productionPanel: productionPanelForProcedure,
    dsoBarChart,
    utilizationApi,
    schedulingPerformanceApi,
    seatUtilizationApi,
    prodPerProviderApi,
  }
}

export type PerformanceDashboard = ReturnType<
  typeof usePerformanceDashboardImpl
>

export function usePerformanceDashboard(
  options: UsePerformanceDashboardOptions,
): PerformanceDashboard {
  return usePerformanceDashboardImpl(options)
}
